// @ts-strict-ignore
import {SectionItem} from 'common/components/items-menu/types';
import {DisplaySize, Placement, TooltipAttrs} from 'common/components/tooltip/types';
import {Translatable} from 'common/i18n';
import {ChartVizType} from 'common/report/constants';
import {FunnelOrder} from 'common/types/reports/bookmark';

export const SEGMENT_SEPARATOR = ` / `;
export const OTHER_PROP = `$other`;
export const OVERALL_PROP = `$overall`;
export const OVERALL_PROP_NAME = `Overall`;
export const OVERALL_PROP_NAME_PAST = `Overall (Past)`;
export const CONVERSION_WINDOW_KEY = `conversionwindow`;

const CONVERSION_HELP_LINK: Translatable = ({t}) =>
  `\n[${t(`common.learn-more`)}](https://docs.mixpanel.com/docs/analysis/reports/funnels#counting-method)`;
const ANY_ORDER_HELP_LINK: Translatable = ({t}) =>
  `\n[${t(`common.learn-more`)}](https://docs.mixpanel.com/docs/reports/funnels#ordering)`;
const commonTooltipAttrs = {size: DisplaySize.Small};

export enum FunnelsCountType {
  Unique = `unique`,
  General = `general`,
  Session = `session`,
  ConversionRate = `conversion_rate`,
  PropertySum = `total`,
}

export const COUNT_TYPE_OPTIONS: SectionItem[] = [
  {
    label: ({t}) => t(`query-builder.count-menu.conversion-aggs.uniques`),
    value: FunnelsCountType.Unique,
    tooltip: {
      text: ({t, print}) => t(`query-builder.count-menu.conversion-aggs.uniques-tooltip`) + print(CONVERSION_HELP_LINK),
      ...commonTooltipAttrs,
    },
  },
  {
    label: ({t}) => t(`query-builder.count-menu.conversion-aggs.totals`),
    value: FunnelsCountType.General,
    tooltip: {
      text: ({t, print}) => t(`query-builder.count-menu.conversion-aggs.totals-tooltip`) + print(CONVERSION_HELP_LINK),
      ...commonTooltipAttrs,
    },
  },
  {
    label: ({t}) => t(`query-builder.count-menu.conversion-aggs.sessions`),
    value: FunnelsCountType.Session,
    tooltip: {
      text: ({t, print}) =>
        t(`query-builder.count-menu.conversion-aggs.sessions-tooltip`) + print(CONVERSION_HELP_LINK),
      ...commonTooltipAttrs,
    },
  },
];

export const ORDER_OPTIONS: SectionItem[] = [
  {
    label: `specific order`,
    value: FunnelOrder.Loose,
    tooltip: {
      text: ({t, print}) => t(`query-builder.conversion.specific-order-tooltip`) + print(ANY_ORDER_HELP_LINK),
      ...commonTooltipAttrs,
    },
  },
  {
    label: `any order`,
    value: FunnelOrder.Any,
    tooltip: {
      text: ({t, print}) => t(`query-builder.conversion.any-order-tooltip`) + print(ANY_ORDER_HELP_LINK),
      ...commonTooltipAttrs,
    },
  },
];

export const CONVERSION_WINDOW_TOOLTIP: TooltipAttrs = {
  text: ({t, print}) => t(`query-builder.conversion-window-tooltip`) + print(CONVERSION_HELP_LINK),
  ...commonTooltipAttrs,
};

export const HOLD_PROPERTY_CONSTANT_TOOLTIP = {
  text: ({t}) => t(`query-builder.conversion.hpc-tooltip`),
  placement: Placement.Right,
  ...commonTooltipAttrs,
};

export const HOLD_PROPERTY_CONSTANT_DISABLED_TOOLTIP = {
  text: ({t}) => t(`query-builder.conversion.hpc-disabled-tooltip`),
  placement: Placement.Right,
  ...commonTooltipAttrs,
};

export const EXCLUSION_EVENTS_TOOLTIP = {
  text: ({t}) => t(`query-builder.conversion.exclude-who-did-tooltip`),
  placement: Placement.Right,
  ...commonTooltipAttrs,
};

export const OPTIMIZED_REENTRY_TOOLTIP = {
  text: ({t}) => t(`query-builder.conversion.optimized-reentry-tooltip`),
  placement: Placement.Right,
  ...commonTooltipAttrs,
};

export const STEPS_VIZ_TYPES = [ChartVizType.Steps, ChartVizType.Metric];
export const FREQUENCY_VIZ_TYPES = [ChartVizType.Bar, ChartVizType.Line];
export const TTC_MEDIAN_VIZ_TYPES = [ChartVizType.Bar, ChartVizType.Metric];
export const TTC_DISTRIBUTION_VIZ_TYPES = [ChartVizType.Bar, ChartVizType.Line];

export const EMPTY_BOOKMARK_NAME = `Untitled Funnels Report`;

export const ATTRIBUTION_OPTIONS: SectionItem[] = [
  {
    label: `Last touch`,
    value: `last_touch`,
    isSelected: true,
  },
  {
    label: `First touch`,
    value: `first_touch`,
    isSelected: false,
  },
];

export const ATTRIBUTION_OPTIONS_REPORT_CONSISTENCY: SectionItem[] = [
  {
    label: ({t}) => t(`query-builder.segment-selection.last-step`),
    value: `last_touch`,
    isSelected: true,
  },
  {
    label: ({t}) => t(`query-builder.segment-selection.first-step`),
    value: `first_touch`,
    isSelected: false,
  },
];

export const ATTRIBUTION_TYPE_TOOLTIPS: {[key: string]: any} = {
  [ATTRIBUTION_OPTIONS[0].value]: {
    text: ({t}) => t(`query-builder.segment-selection.last-step-tooltip`),
    size: DisplaySize.Small,
  },
  [ATTRIBUTION_OPTIONS[1].value]: {
    text: ({t}) => t(`query-builder.segment-selection.first-step-tooltip`),
    size: DisplaySize.Small,
  },
};

/** All metrics that can be shown in the table */
export enum TableToggleableMetrics {
  StatSig = `stat-sig`,
  ConvFirstStep = `conv-first-step`,
  ConvPrevStep = `conv-prev-step`,
  Count = `count`,
  TimeFirstStep = `time-first-step`,
  TimePrevStep = `time-prev-step`,
}

export const TABLE_COLUMN_SELECTOR_DISPLAY_NAME: {[key in TableToggleableMetrics]: Translatable} = {
  [TableToggleableMetrics.StatSig]: ({t}) => t(`reports.select-table-column.stat-sig`),
  [TableToggleableMetrics.Count]: ({t}) => t(`reports.select-table-column.step-count`),
  [TableToggleableMetrics.TimePrevStep]: ({t}) => t(`reports.select-table-column.time-prev-step`),
  [TableToggleableMetrics.TimeFirstStep]: ({t}) => t(`reports.select-table-column.time-first-step`),
  [TableToggleableMetrics.ConvPrevStep]: ({t}) => t(`reports.select-table-column.convert-prev-step`),
  [TableToggleableMetrics.ConvFirstStep]: ({t}) => t(`reports.select-table-column.convert-first-step`),
};

export const TABLE_COLUMN_SELECTOR_TOOLTIPS: {[key in TableToggleableMetrics]: Translatable} = {
  [TableToggleableMetrics.StatSig]: ({t}) => t(`reports.select-table-column.tooltips.stat-sig`),
  [TableToggleableMetrics.Count]: ({t}) => t(`reports.select-table-column.tooltips.step-count`),
  [TableToggleableMetrics.TimePrevStep]: ({t}) => t(`reports.select-table-column.tooltips.time-prev-step`),
  [TableToggleableMetrics.TimeFirstStep]: ({t}) => t(`reports.select-table-column.tooltips.time-first-step`),
  [TableToggleableMetrics.ConvPrevStep]: ({t}) => t(`reports.select-table-column.tooltips.convert-prev-step`),
  [TableToggleableMetrics.ConvFirstStep]: ({t}) => t(`reports.select-table-column.tooltips.convert-first-step`),
};
